/**
 * Iter XX.97 — Cricket Memory Confirmation Card.
 *
 * Rendered beneath an assistant bubble when the server flagged a
 * save-intent turn. Three visual states, one component:
 *
 *   · saving  — brief placeholder while the background extraction
 *               runs. Chat.jsx polls /web/memory/parent_child_profile
 *               and mutates the payload in-place when the extraction
 *               lands `saved` or `failed`.
 *   · saved   — the founder-brief card: "Cricket Memory Updated"
 *               header + Player / Role / Environment / Patterns /
 *               Goals sections. Newly-saved bullets are highlighted;
 *               already-known bullets sit dimmer.
 *   · failed  — the graceful fallback line (no card body).
 *
 * Pure presentational — never fires network. Payload is:
 *
 *   {
 *     status:        "saving" | "saved" | "failed",
 *     save_id?:      string,
 *     newly_saved?:  [{ section, label, value, was? }],
 *     already_known?:[{ section, label, value }],
 *     profile?:      { player, role, environment,
 *                      current_patterns, current_goals },
 *   }
 */
import React from "react";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const SECTION_ORDER = [
  { key: "player",           title: "Player",           iconLabel: "🏏" },
  { key: "role",             title: "Current Role",     iconLabel: "🎯" },
  { key: "environment",      title: "Environment",      iconLabel: "🌱" },
  { key: "current_patterns", title: "Current Patterns", iconLabel: "🔁" },
  { key: "current_goals",    title: "Active Goals",     iconLabel: "⭐" },
];

function _renderSection(profile, section, newlyKeysBySection) {
  const { key, title } = section;
  const isPatternList = key === "current_patterns";
  const isGoalList = key === "current_goals";
  const items = [];
  if (isPatternList) {
    (profile.current_patterns || []).forEach((p, i) => {
      if (!p || !p.fact) return;
      items.push({ label: p.fact, context: p.context, hypothesis: p.hypothesis, key: `p${i}` });
    });
  } else if (isGoalList) {
    (profile.current_goals || []).forEach((g, i) => {
      if (!g) return;
      items.push({ label: String(g), key: `g${i}` });
    });
  } else {
    const sect = profile[key] || {};
    Object.entries(sect).forEach(([k, v]) => {
      if (!v) return;
      const labelMap = {
        name:             "Name",
        age:              "Age",
        handedness:       "Handedness",
        batting_position: "Batting position",
        bowling_style:    "Bowling style",
        academy:          "Academy",
        level:            "Level",
        pitch_type:       "Pitch",
      };
      items.push({ label: labelMap[k] || k, value: String(v), key: k });
    });
  }
  if (!items.length) return null;
  const newlyForSection = newlyKeysBySection[key] || new Set();
  return (
    <div key={key} className="mt-3" data-testid={`cricket-memory-section-${key}`}>
      <div className="text-[11px] uppercase tracking-wider text-emerald-300/80 font-medium mb-1.5">
        {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((it) => {
          const marker = newlyForSection.has(
            (it.label || it.value || "").toLowerCase(),
          ) ? "new" : "known";
          return (
            <li key={it.key}
                data-testid={`cricket-memory-item-${key}-${it.key}`}
                data-status={marker}
                className="flex items-start gap-2 text-sm">
              <span className={
                marker === "new"
                  ? "mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400 flex-shrink-0"
                  : "mt-1.5 h-1.5 w-1.5 rounded-full bg-neutral-500 flex-shrink-0"
              }/>
              <div className="flex-1 min-w-0">
                {isPatternList ? (
                  <>
                    <div className={marker === "new" ? "text-neutral-100" : "text-neutral-300"}>
                      {it.label}
                    </div>
                    {it.context && (
                      <div className="text-xs text-neutral-400 mt-0.5">
                        Context: {it.context}
                      </div>
                    )}
                    {it.hypothesis && (
                      <div className="text-xs text-neutral-400 mt-0.5 italic">
                        Working hypothesis: {it.hypothesis}
                      </div>
                    )}
                  </>
                ) : (
                  <div className={marker === "new" ? "text-neutral-100" : "text-neutral-300"}>
                    {it.value
                      ? <><span className="text-neutral-400">{it.label}: </span>{it.value}</>
                      : it.label}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function CricketMemoryConfirmationCard({ payload }) {
  if (!payload) return null;
  const status = payload.status || "saving";
  if (status === "saving") {
    return (
      <div
        data-testid="cricket-memory-card-saving"
        className="mt-2 max-w-[85%] rounded-xl border border-emerald-900/50 bg-emerald-950/30 px-4 py-3 flex items-center gap-2.5">
        <Loader2 size={16} className="text-emerald-400 animate-spin flex-shrink-0" />
        <span className="text-sm text-emerald-100/90">
          Saving to Cricket Memory…
        </span>
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div
        data-testid="cricket-memory-card-failed"
        className="mt-2 max-w-[85%] rounded-xl border border-amber-900/40 bg-amber-950/20 px-4 py-3 flex items-start gap-2.5">
        <AlertCircle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <span className="text-sm text-amber-100/90 leading-relaxed">
          {"I'll remember what you told me, but I couldn't structure the "
           + "details right now. Feel free to share them again when it's "
           + "convenient."}
        </span>
      </div>
    );
  }
  // saved
  const profile = payload.profile || {};
  const newlyKeysBySection = {};
  (payload.newly_saved || []).forEach((n) => {
    if (!n || !n.section) return;
    const k = (n.value || n.label || "").toLowerCase();
    if (!newlyKeysBySection[n.section]) newlyKeysBySection[n.section] = new Set();
    // For scalar sections, we key by label (e.g. "Name", "Age"); for
    // list sections we key by fact/goal value.
    if (["player", "role", "environment"].includes(n.section)) {
      newlyKeysBySection[n.section].add((n.label || "").toLowerCase());
    } else {
      newlyKeysBySection[n.section].add(k);
    }
  });
  const anythingNew = (payload.newly_saved || []).length > 0;
  return (
    <div
      data-testid="cricket-memory-card-saved"
      className="mt-2 max-w-[92%] rounded-2xl border border-emerald-800/60 bg-gradient-to-br from-emerald-950/40 to-neutral-950/80 px-4 py-3.5 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <CheckCircle2 size={18} className="text-emerald-400" />
        <div className="text-sm font-semibold text-emerald-100">
          Cricket Memory Updated
        </div>
      </div>
      <div className="text-xs text-neutral-400 leading-relaxed">
        {anythingNew
          ? "I'll remember this for future conversations."
          : "Everything you shared is already in your Cricket Memory."}
      </div>
      {SECTION_ORDER.map((s) => _renderSection(profile, s, newlyKeysBySection))}
      <div className="mt-3 pt-2 border-t border-emerald-900/40 text-[11px] text-neutral-500 flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400"/>
        <span>Newly saved</span>
        <span className="mx-1.5 text-neutral-700">·</span>
        <span className="h-1.5 w-1.5 rounded-full bg-neutral-500"/>
        <span>Already known</span>
      </div>
    </div>
  );
}
